import { Component, Input, OnInit } from '@angular/core';
import { CRMItem, CrmItemSnapshot } from '../../models/crm/CRMItem.model';
import { CRMStatusLabels } from '../../models/crm/CRMStatus';
import { CourtshipTypeLabels } from '../../enums/courtship-type.enum';
import { CRMCompanyStormLabels } from '../../models/crm/CRMCompanyStorm';
import { CRMItemHistory, StandardCRMItemHistoryEventTypeLabels } from '../../models/crm/CRMItemHistory.model';
import { CrmService } from '../../../core/data-services/crm.service';
import { TextContent } from '../../models';
import { HistoryComparison, CrmHistoryUtilsService } from '../../../core/services/crm-history-utils.service';
import { CrmStatusService } from '../../../core/data-services/crm-status.service';

@Component({
    selector: 'rms-crm-item-snapshot',
    templateUrl: './crm-item-snapshot.component.html',
    styleUrls: ['./crm-item-snapshot.component.scss'],
    standalone: false
})
export class CrmItemSnapshotComponent implements OnInit {

  @Input() item: CRMItem;
  @Input() note: {id: number, textContent: TextContent, itemSnapshot: CrmItemSnapshot};

  public snapshot: CrmItemSnapshot;
  public createdDate: Date;

  public history: Array<CRMItemHistory>;
  public historyComparison: Array<HistoryComparison> = new Array<HistoryComparison>();
  public deltaString: string;

  public readonly statusLabels = CRMStatusLabels;
  public readonly courtshipLabels = CourtshipTypeLabels;
  public readonly stormLabels = CRMCompanyStormLabels;
  public readonly historyEventTypeLabels = StandardCRMItemHistoryEventTypeLabels;

  constructor(
    private crmService: CrmService,
    private crmHistoryUtilsService: CrmHistoryUtilsService,
    public crmStatusService: CrmStatusService,
  ) { }

  ngOnInit() {
    this.snapshot = this.note.itemSnapshot;
    this.createdDate = new Date(this.snapshot.created);
    this.loadHistory();
  }

  public hasDelta(): boolean {
    return this.historyComparison.length > 0;
  }

  public isFirstNote() {
    return this.crmService.NEW_ITEM_NOTE_CONTENT === this.note.textContent.content;
  }

  private loadHistory() {
    this.crmService.getCrmItemHistory(this.item.id).subscribe((history: Array<CRMItemHistory>) => {
      this.history = history;
      this.setHistoryComparison();
    }, () => {});
  }

  /**
   * Set the history comparison to only events that are relevant to this note:
   * 1. Within 10 seconds of the note creation time
   * 2. Valid event type
   * 3. Changed by the same user that created the note
   */
  private setHistoryComparison() {
    this.historyComparison = this.crmHistoryUtilsService.getCrmItemHistoryComparison(this.history);
    this.historyComparison = this.historyComparison.filter(value =>
      (Math.abs(new Date(value.itemAfter.eventTimestamp).getTime() - this.createdDate.getTime()) / 1000) < 10 &&
      this.crmHistoryUtilsService.isTimestampEventType(value.itemAfter.eventType) &&
      (value.itemAfter.eventChangedBy === this.note.textContent.createdBy.id));
    this.historyComparison.sort((a, b) => a.itemAfter.eventTimestamp < b.itemAfter.eventTimestamp ? 1 : -1);
    this.consolidateEventTypes();
    this.setDeltaString();
  }


  
  /**
    * Consolidates multiple events created within the same note into an array, 
    * keeping only the most recent event of each type.
    */
  private consolidateEventTypes() {
    const historyMap = new Map<string, HistoryComparison>();
    this.historyComparison.forEach(value => {
      const key = value.itemAfter.eventType;
      // If the event type doesnt exist, add to the map
      // If the event type does exist, compare timestamps and keep the most recent
      if (!historyMap.has(key)) {
        historyMap.set(key, value);
      } else {
        const existing = historyMap.get(key);
        if (new Date(value.itemAfter.eventTimestamp) > new Date(existing.itemAfter.eventTimestamp)) {
          historyMap.set(key, value);
        }
      }
    });
    // Convert the map back to an array
    this.historyComparison = Array.from(historyMap.values());
  }

  private setDeltaString() {
    const delta = new Array<string>();
    this.historyComparison.forEach(value => {
           delta.push(`${this.historyEventTypeLabels[value.itemAfter.eventType]} changed from 
<span class="bold">${this.crmHistoryUtilsService.getCrmItemHistoryDescription(value.itemBefore)}</span> to 
<span class="bold">${this.crmHistoryUtilsService.getCrmItemHistoryDescription(value.itemAfter)}</span>`);
    });
    this.deltaString = delta.join('; ');
  }

}
